import { Injectable } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { SubscriptionService } from "src/app/core/service/subscription.service";
import { CookiesDialogComponent } from "src/app/shared/component/cookies-dialog/cookies-dialog.component";
import { BillDTO } from "../../schema/Billing/Bills/BillDTO";
import { UserCustomerDTO } from "../../schema/Billing/Customer/UserCustomerDTO";
import { CourseApresentationDTO } from "../../schema/Courses/CourseApresentationDTO";
import { CursoTO } from "../../schema/Courses/CursoTO";
import { SubscriptionDTO } from "../../schema/Subscriptions/SubscriptionDTO";
import { UsuarioTO } from "../../schema/User/UsuarioTO";
import { FacebookPixelService } from "../facebook-pixel/facebook-pixel.service";
import { GoogleAnalyticsService } from "../google-analytics/google-analytics.service";

@Injectable({
    providedIn: "root"
})
export class DataAnalysisService {
    private subscription: SubscriptionDTO;
    private pixelId: string;
    private accepted?: boolean = null;

    private googleAnalyticsId: string;

    constructor(
        private subscriptionService: SubscriptionService,
        private facebookPixelService: FacebookPixelService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private dialog: MatDialog
    ) {
        this.subscriptionService.currentSubscriptionAsync().subscribe((sub) => {
            this.subscription = sub;
            if (this.googleAnalyticsActive()) this.googleAnalyticsService.load();

            const hasCookies =
                this.subscription?.dataAnalysisSettings?.facebookPixel?.active ||
                this.subscription?.dataAnalysisSettings?.googleAnalytics?.active;

            this.accepted = JSON.parse(localStorage.getItem("cookies-accepted"));

            if (!this.accepted && hasCookies && !this.dialog.getDialogById("cookiesDialog")) {
                const config: MatDialogConfig = {
                    hasBackdrop: false,
                    id: "cookiesDialog",
                    position: { bottom: "10px" },
                    panelClass: "cookies-dialog"
                };

                const dialogRef = this.dialog.open(CookiesDialogComponent, config);

                dialogRef.afterClosed().subscribe((result) => {
                    if (!result) {
                        this.pixelId = null;
                        this.googleAnalyticsId = null;
                        localStorage.setItem("cookies-accepted", "false");
                        return;
                    }

                    localStorage.setItem("cookies-accepted", "true");
                });
            }
        });
    }

    pixelActive(): boolean {
        return (
            this.subscription?.dataAnalysisSettings?.facebookPixel?.active == true &&
            this.pixelId != "" &&
            window.location.hostname != "localhost"
        );
    }

    googleAnalyticsActive(): boolean {
        return (
            this.subscription?.dataAnalysisSettings?.googleAnalytics?.active == true &&
            this.googleAnalyticsId != "" &&
            window.location.hostname != "localhost"
        );
    }

    viewDetailCatalog(course: CursoTO): void {
        try {
            if (this.pixelActive())
                this.facebookPixelService.viewContent("Catálogo", course.titulo, course.type, course.fixedPrice, null);

            if (this.googleAnalyticsActive())
                this.googleAnalyticsService.ViewItem(
                    course.fixedPrice,
                    course.id,
                    course.titulo,
                    course.categoria,
                    "Catálogo",
                    null
                );
        } catch (error) {
            console.log("erro ao enviar evento viewDetailCatalog " + error);
        }
    }

    viewCoursePresentation(course: CourseApresentationDTO, userEmail: string): void {
        try {
            if (this.pixelActive())
                this.facebookPixelService.viewContent("Apresentação", course.titulo, course.type, course.fixedPrice, [
                    userEmail
                ]);

            if (this.googleAnalyticsActive())
                this.googleAnalyticsService.ViewItem(
                    course.fixedPrice,
                    course.id,
                    course.titulo,
                    course.categoria,
                    "Apresentação",
                    userEmail
                );
        } catch (error) {
            console.log("erro ao enviar evento viewCoursePresentation " + error);
        }
    }

    login(email: string, usernames: string[], name: string, method?: string): void {
        try {
            if (this.pixelActive()) this.facebookPixelService.Login(email, usernames, name, method);

            if (this.googleAnalyticsActive()) this.googleAnalyticsService.Login(email, usernames, name, method);
        } catch (error) {
            console.log("erro ao enviar evento login " + error);
        }
    }

    contact(): void {
        if (this.pixelActive()) this.facebookPixelService.Contact();
    }

    viewCourseContent(course: CursoTO, currentLesson: string, userEmail: string): void {
        try {
            if (this.pixelActive())
                this.facebookPixelService.viewContent(
                    "Aulas",
                    course.titulo,
                    course.type,
                    course.fixedPrice,
                    [userEmail],
                    currentLesson
                );

            if (this.googleAnalyticsActive())
                this.googleAnalyticsService.ViewCourseContent(
                    course.titulo,
                    course.id,
                    course.categoria,
                    currentLesson,
                    userEmail
                );
        } catch (error) {
            console.log("erro ao enviar evento viewCourseContent " + error);
        }
    }

    freeCourseRegistration(course: CourseApresentationDTO, user: UsuarioTO): void {
        try {
            if (this.pixelActive())
                this.facebookPixelService.freeCourseRegistration(
                    course.titulo,
                    course.id,
                    user.email,
                    user.nome + " " + user.sobrenome
                );

            if (this.googleAnalyticsActive())
                this.googleAnalyticsService.freeCourseRegistration(
                    course.titulo,
                    course.id,
                    user.email,
                    user.nome + " " + user.sobrenome
                );
        } catch (error) {
            console.log("erro ao enviar evento freeCourseRegistration " + error);
        }
    }

    searchCatolog(search: string): void {
        try {
            if (this.pixelActive()) this.facebookPixelService.Search("Catálogo", search);
            if (this.googleAnalyticsActive()) this.googleAnalyticsService.Search("Catálogo", search);
        } catch (error) {
            console.log("erro ao enviar evento searchCatolog " + error);
        }
    }

    searchAprender(search: string, userEmail: string): void {
        try {
            if (this.pixelActive()) this.facebookPixelService.Search("Aprender", search, userEmail);
            if (this.googleAnalyticsActive()) this.googleAnalyticsService.Search("Aprender", search, userEmail);
        } catch (error) {
            console.log("erro ao enviar evento searchAprender " + error);
        }
    }

    purchaseProduct(curso: CursoTO, bill: BillDTO, user: UsuarioTO, coupon?: string): void {
        try {
            if (this.pixelActive())
                this.facebookPixelService.Purchase(curso?.nome, "product", "BRL", bill.amount, {
                    userName: user.nome + " " + user.sobrenome,
                    email: user.email,
                    billId: bill.id,
                    coupon
                });

            if (this.googleAnalyticsActive())
                this.googleAnalyticsService.Purchase(
                    bill.id,
                    bill.amount,
                    curso?.id,
                    curso?.nome,
                    "product",
                    user.email,
                    coupon
                );
        } catch (error) {
            console.log("erro ao enviar evento purchaseProduct " + error);
        }
    }

    initiateCheckout(course: CursoTO): void {
        try {
            if (this.pixelActive()) this.facebookPixelService.InitiateCheckout(course.nome, "BRL", course.fixedPrice);

            if (this.googleAnalyticsActive())
                this.googleAnalyticsService.BeginCheckout(course.fixedPrice, course.id, course.nome, "product");
        } catch (error) {
            console.log("erro ao enviar evento initiateCheckout " + error);
        }
    }

    AddPaymentInfo(method: string, course: CursoTO, customer: UserCustomerDTO, coupon?: string): void {
        try {
            if (this.pixelActive())
                this.facebookPixelService.AddPaymentInfo(method, {
                    userName: customer.name,
                    email: customer.email,
                    productId: course.id,
                    productName: course.nome
                });

            if (this.googleAnalyticsActive())
                this.googleAnalyticsService.AddPaymentInfo(
                    course.fixedPrice,
                    method,
                    course.id,
                    course.nome,
                    "product",
                    customer.email,
                    coupon
                );
        } catch (error) {
            console.log("erro ao enviar evento AddPaymentInfo " + error);
        }
    }

    CompleteRegistration(registrationBy: string, name: string, userEmail: string, status: boolean): void {
        try {
            if (this.pixelActive())
                this.facebookPixelService.CompleteRegistration(status, registrationBy, {
                    name: name,
                    email: userEmail
                });

            if (this.googleAnalyticsActive())
                this.googleAnalyticsService.CompleteRegistration(status, registrationBy, name, userEmail);
        } catch (error) {
            console.log("erro ao enviar evento CompleteRegistration " + error);
        }
    }
}
